import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, startWith, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  Holidays as holidaysOptions,
  OrderOptions as orderNames,
  Parashot as parashotOptions,
  type OrderOption,
  type ParashaOption,
} from '@shared/constants';
import { UserType, UserTypeWithOrders } from '../../../../../../shared/schemas/userSchema';
import { SnackBarService } from '../../../services/snack-bar.service';
import {
  addUserToStore,
  updateUserinStore,
} from '../../../store/slices/usersSlice/usersSlice.actions';
import {
  selectAllUsers,
  selectCurrentUserName,
} from '../../../store/slices/usersSlice/usersSlice.selector';
import { AppStore } from '../../../store/store';
import { UsersService } from '../services/users.service';
import { OrdersService } from '../services/orders.service';
import labels from './../data/addOrder.json';

export type OrderNamesType = OrderOption;

@Component({
  selector: 'app-add-order',
  templateUrl: './add-order.component.html',
  styleUrl: './add-order.component.scss',
})
export class AddOrderComponent implements OnInit, OnDestroy {
  loading = false;

  users$: Observable<UserType[]> = this.store.pipe(select(selectAllUsers));
  currentUserName$: Observable<string> = this.store.pipe(select(selectCurrentUserName));
  adminName = '';

  orderNamesOptions: readonly OrderOption[] = orderNames;
  parashotOptions: readonly ParashaOption[] = parashotOptions;
  holidayOptions: readonly ParashaOption[] = holidaysOptions;

  header: string = labels.header;
  dateVal: Date = new Date();

  addOrder: FormGroup = new FormGroup({
    name: new FormControl([], [Validators.required, Validators.minLength(1)]),
    userId: new FormControl('', [Validators.required]),
    parasha: new FormControl('', [Validators.required, Validators.minLength(2)]),
    price: new FormControl(0, [Validators.required, Validators.min(0)]),
    pricePaid: new FormControl(0, [Validators.required, Validators.min(0)]),
    beenPaid: new FormControl(false, [Validators.required]),
    orderInvoice: new FormControl('', []),
    note: new FormControl('', null),
  });

  // ── User picker search ─────────────────────────────────────────────
  userSearchTerm = '';
  private userSearch$ = new BehaviorSubject<string>('');
  filteredUsers$!: Observable<UserTypeWithOrders[]>;

  // ── Order confirmation dialog ──────────────────────────────────────
  // Captures a snapshot of the form data + selected user + adminName at
  // the moment "submit" is clicked. The actual write only happens after
  // the admin confirms in the dialog.
  confirmingOrder: {
    user: UserTypeWithOrders | null;
    parasha: string;
    items: string[];
    price: number;
    pricePaid: number;
    orderInvoice: string;
    note: string;
    adminName: string;
  } | null = null;

  // ── Parasha / holiday autocomplete ─────────────────────────────────
  parashaFilterCtrl: FormControl = new FormControl('');
  filteredParashotOptions!: Observable<ParashaOption[]>;
  filteredHolidaysOptions!: Observable<ParashaOption[]>;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppStore>,
    private orderService: OrdersService,
    private userService: UsersService,
    private snackBar: SnackBarService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // Always fetch — the "if empty" guard was unreliable after session restore.
    this.fetchUsers();

    // Search filter: combine the store list with the search term.
    this.filteredUsers$ = combineLatest([
      this.store.pipe(select(selectAllUsers)),
      this.userSearch$,
    ]).pipe(
      map(([users, term]) => this.filterUsers(users, term)),
    );

    // Parasha + holiday autocomplete.
    this.filteredParashotOptions = this.parashaFilterCtrl.valueChanges.pipe(
      startWith(''),
      map((search) => this._filterParashot(search ?? '')),
    );
    this.filteredHolidaysOptions = this.parashaFilterCtrl.valueChanges.pipe(
      startWith(''),
      map((search) => this._filterHolidays(search ?? '')),
    );

    // Pre-select a user when arrived via "new order for this user" link.
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const userIdParam = params.get('userId');
      if (userIdParam) this.selectUser(userIdParam);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Fetching ───────────────────────────────────────────────────────

  private fetchUsers(): void {
    this.userService
      .getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fetched) =>
          fetched.forEach((user) => this.store.dispatch(addUserToStore({ user }))),
        error: (err) => console.error('Error fetching users:', err),
      });
  }

  // ── Search ─────────────────────────────────────────────────────────

  onSearchChange(value: string): void {
    this.userSearchTerm = value;
    this.userSearch$.next(value);
  }

  clearSearch(): void {
    this.userSearchTerm = '';
    this.userSearch$.next('');
  }

  private filterUsers(
    users: readonly UserTypeWithOrders[],
    term: string,
  ): UserTypeWithOrders[] {
    const t = (term ?? '').trim().toLowerCase();
    const sorted = [...users].sort((a, b) =>
      (a.lastName ?? '').localeCompare(b.lastName ?? '', 'he'),
    );
    if (!t) return sorted;
    return sorted.filter((u) => {
      const full = `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase();
      return (
        full.includes(t) ||
        (u.firstName ?? '').toLowerCase().includes(t) ||
        (u.lastName ?? '').toLowerCase().includes(t)
      );
    });
  }

  // ── Parasha filtering ──────────────────────────────────────────────

  private _filterParashot(value: string): ParashaOption[] {
    const filterValue = value?.toLowerCase() || '';
    return this.parashotOptions.filter(
      (option) =>
        option.eng.toLowerCase().includes(filterValue) ||
        option.heb.includes(filterValue),
    );
  }

  private _filterHolidays(value: string): ParashaOption[] {
    const filterValue = value?.toLowerCase() || '';
    return this.holidayOptions.filter(
      (option) =>
        option.eng.toLowerCase().includes(filterValue) ||
        option.heb.includes(filterValue),
    );
  }

  displayFn(option: ParashaOption | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.heb;
  }

  selectOption(option: ParashaOption): void {
    this.addOrder.get('parasha')?.setValue(option.heb);
  }

  selectUser(userId: string): void {
    this.addOrder.get('userId')?.setValue(userId);
  }

  /**
   * Only "זיכוי" selected → a credit: no cost field, and the amount field is
   * labelled "סכום". (The old template compared the array to a string, so
   * the label never switched.)
   */
  get isCreditOnly(): boolean {
    const names = this.addOrder.get('name')?.value;
    return Array.isArray(names) && names.length === 1 && names[0] === 'זיכוי';
  }

  // ── Submit (two-step: open confirm dialog → confirm → write) ──────

  /**
   * Step 1: validate the form, snapshot it into `confirmingOrder`, and
   * show the confirmation modal. No network call yet.
   */
  async onSubmit(): Promise<void> {
    if (this.addOrder.invalid || this.loading) return;

    const formData = this.addOrder.value;

    // Resolve the parasha to its Hebrew form.
    const selectedParasha = this.parashaFilterCtrl.value;
    const parashaHeb =
      (selectedParasha && typeof selectedParasha === 'object'
        ? selectedParasha.heb
        : selectedParasha) ||
      formData.parasha ||
      '';

    if (!parashaHeb || parashaHeb.length < 2) {
      this.snackBar.openSnackBarError('יש לבחור פרשה או חג', 'x');
      return;
    }

    // Look up the selected user from the latest store snapshot.
    const users = await firstValueFrom(
      this.users$ as unknown as Observable<UserTypeWithOrders[]>,
    );
    const user = users.find((u) => u._id === formData.userId) ?? null;
    if (!user) {
      this.snackBar.openSnackBarError('יש לבחור מתפלל מהרשימה', 'x');
      return;
    }

    const adminName = await firstValueFrom(this.currentUserName$);

    this.confirmingOrder = {
      user,
      parasha: parashaHeb,
      items: Array.isArray(formData.name) ? formData.name : [formData.name].filter(Boolean),
      price: Number(formData.price) || 0,
      pricePaid: Number(formData.pricePaid) || 0,
      orderInvoice: formData.orderInvoice || '',
      note: formData.note || '',
      adminName: adminName || '',
    };
  }

  /** Step 2a: admin cancels the confirmation dialog. */
  cancelOrderConfirm(): void {
    this.confirmingOrder = null;
  }

  /** Step 2b: admin confirms — actually write the order. */
  async confirmOrderSubmit(): Promise<void> {
    if (!this.confirmingOrder || this.loading) return;
    this.loading = true;

    const snap = this.confirmingOrder;

    try {
      const payload = {
        name: snap.items,
        userId: snap.user!._id,
        parasha: snap.parasha,
        price: snap.price,
        pricePaid: snap.pricePaid,
        beenPaid: snap.pricePaid >= snap.price,
        orderInvoice: snap.orderInvoice || undefined,
        note: snap.note || undefined,
        doneBy: snap.adminName,
      };

      const orderRes = await this.orderService.addOrder(payload);
      this.store.dispatch(updateUserinStore({ user: orderRes }));

      this.addOrder.reset({
        name: [],
        userId: '',
        parasha: '',
        price: 0,
        pricePaid: 0,
        beenPaid: false,
        orderInvoice: '',
        note: '',
      });
      this.parashaFilterCtrl.setValue('');
      this.confirmingOrder = null;
      this.snackBar.openToast('ההזמנה נוצרה בהצלחה', {
        kind: 'success',
        description: `${snap.user!.firstName} ${snap.user!.lastName} · ${snap.parasha}`,
      });
    } catch (error) {
      console.error(error);
      this.snackBar.openToast('שגיאה בהזנת ההזמנה', {
        kind: 'error',
        description: 'הנתונים לא נשמרו. נסה שוב או פנה למנהל המערכת.',
      });
    } finally {
      this.loading = false;
    }
  }
}
